import { useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Dialog } from "primereact/dialog";
import Button from "../../../componentes/button/button";
import ContasAPagarService from "../../../services/resource/contasAPagarService";
import { formatarData, formatarMoeda } from "../../../utils/solicitacoes/formatters";

const service = new ContasAPagarService();

const CONFIANCA_INFO = {
    ALTA: { label: "Alta", className: "bg-success/10 text-success" },
    MEDIA: { label: "Média", className: "bg-amber-100 text-amber-700" },
    BAIXA: { label: "Baixa", className: "bg-destructive/10 text-destructive" },
};

const FILTROS = [
    { chave: "todas", label: "Todas" },
    { chave: "sugeridas", label: "Sugeridas" },
    { chave: "conferir", label: "Para conferir" },
    { chave: "sem", label: "Sem correspondência" },
    { chave: "vinculadas", label: "Já vinculadas" },
];

/** Número da solicitação como link que abre a solicitação em outra aba. */
function LinkSolicitacao({ id, numero, className = "" }) {
    if (!id) return <span>{numero || "-"}</span>;
    return (
        <a
            href={`/solicitacoes/${id}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Abrir a solicitação em outra aba"
            className={`text-primary font-semibold hover:underline inline-flex items-center gap-1 ${className}`}
        >
            {numero || `#${id}`}
            <i className="fa fa-arrow-up-right-from-square text-[10px]" />
        </a>
    );
}

function Selo({ confianca, ambigua }) {
    const info = CONFIANCA_INFO[confianca];
    return (
        <span className="inline-flex flex-wrap items-center gap-1">
            {info && (
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${info.className}`}>{info.label}</span>
            )}
            {ambigua && (
                <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-700"
                    title="Há outra solicitação com pontuação equivalente">Empate</span>
            )}
        </span>
    );
}

/**
 * Concilia um extrato bancário (Excel) com as solicitações aguardando Confirmação de Pagamento. A análise
 * não grava nada; só "Vincular" grava (e, se marcado, confirma o pagamento das solicitações vinculadas).
 */
export default function ConciliacaoExtrato({ aoAlterar }) {
    const inputArquivo = useRef(null);
    const [arquivo, setArquivo] = useState(null);
    const [analise, setAnalise] = useState(null);
    const [analisando, setAnalisando] = useState(false);
    // escolhas por linha: { [linha]: { solicitacaoId, marcada } }
    const [escolhas, setEscolhas] = useState({});
    const [filtro, setFiltro] = useState("todas");
    const [confirmando, setConfirmando] = useState(false);
    const [confirmarPagamento, setConfirmarPagamento] = useState(false);
    const [comentario, setComentario] = useState("");
    const [vinculando, setVinculando] = useState(false);
    const [falhas, setFalhas] = useState(null);

    function iniciarEscolhas(dados) {
        const inicial = {};
        dados.linhas.forEach(l => {
            if (l.situacao === "SUGERIDA") {
                inicial[l.linha] = { solicitacaoId: l.sugerida?.solicitacaoId ?? null, marcada: l.marcadaPorPadrao };
            }
        });
        setEscolhas(inicial);
    }

    function analisar(arq = arquivo) {
        if (!arq) {
            toast.error("Selecione o extrato bancário em Excel.");
            return;
        }
        setAnalisando(true);
        service.analisarExtrato(arq)
            .then(res => {
                setAnalise(res.data);
                iniciarEscolhas(res.data);
                setFiltro("todas");
            })
            .catch(() => { /* mensagem do backend exibida pelo interceptor */ })
            .finally(() => setAnalisando(false));
    }

    // Opções de cada linha: a sugerida e as alternativas (todas com o mesmo valor do débito).
    function opcoesDaLinha(l) {
        const lista = [];
        if (l.sugerida) lista.push({ ...l.sugerida, pontos: l.pontos, confianca: l.confianca, criterio: l.criterio });
        (l.alternativas || []).forEach(a => lista.push({ ...a.solicitacao, pontos: a.pontos, confianca: a.confianca, criterio: a.criterio }));
        return lista;
    }

    const linhasVisiveis = useMemo(() => {
        if (!analise) return [];
        return analise.linhas.filter(l => {
            switch (filtro) {
                case "sugeridas": return l.situacao === "SUGERIDA";
                case "conferir": return l.situacao === "SUGERIDA" && (!l.marcadaPorPadrao || l.ambigua);
                case "sem": return l.situacao === "SEM_CORRESPONDENCIA";
                case "vinculadas": return l.situacao === "JA_VINCULADA";
                default: return true;
            }
        });
    }, [analise, filtro]);

    const selecionadas = useMemo(() => (analise?.linhas || [])
        .filter(l => l.situacao === "SUGERIDA" && escolhas[l.linha]?.marcada && escolhas[l.linha]?.solicitacaoId)
        .map(l => ({ linha: l, solicitacaoId: escolhas[l.linha].solicitacaoId })), [analise, escolhas]);

    const repetidas = useMemo(() => {
        const vistos = new Set();
        const dup = new Set();
        selecionadas.forEach(s => { if (vistos.has(s.solicitacaoId)) dup.add(s.solicitacaoId); vistos.add(s.solicitacaoId); });
        return dup;
    }, [selecionadas]);

    function alternarMarca(l) {
        setEscolhas(prev => ({ ...prev, [l.linha]: { ...prev[l.linha], marcada: !prev[l.linha]?.marcada } }));
    }

    function trocarSolicitacao(l, valor) {
        const id = valor === "" ? null : Number(valor);
        setEscolhas(prev => ({ ...prev, [l.linha]: { solicitacaoId: id, marcada: id != null } }));
    }

    function marcarConfiaveis() {
        setEscolhas(prev => {
            const proximo = { ...prev };
            analise.linhas.forEach(l => {
                if (l.situacao === "SUGERIDA") {
                    proximo[l.linha] = { solicitacaoId: l.sugerida?.solicitacaoId ?? null, marcada: l.marcadaPorPadrao };
                }
            });
            return proximo;
        });
    }

    function pedirConfirmacao() {
        if (selecionadas.length === 0) {
            toast.error("Marque ao menos uma linha para vincular.");
            return;
        }
        if (repetidas.size > 0) {
            toast.error("Há solicitações escolhidas para mais de uma linha. Ajuste antes de vincular.");
            return;
        }
        setConfirmando(true);
    }

    function vincular() {
        setVinculando(true);
        const itens = selecionadas.map(({ linha, solicitacaoId }) => {
            const opcao = opcoesDaLinha(linha).find(o => o.solicitacaoId === solicitacaoId);
            return {
                solicitacaoId,
                dataPagamento: linha.dataPagamento,
                valor: linha.valor,
                historico: linha.historico,
                detalhamento: linha.detalhamento,
                ocorrencia: linha.ocorrencia,
                confianca: opcao?.pontos ?? null,
                criterio: opcao?.criterio ?? null,
            };
        });
        service.vincularExtrato({
            itens,
            confirmarPagamento,
            comentario: comentario.trim() || null,
            arquivo: analise.arquivo,
        })
            .then(res => {
                const resultados = res.data;
                const ok = resultados.filter(r => r.vinculado).length;
                const pagos = resultados.filter(r => r.pagamentoConfirmado).length;
                const problemas = resultados.filter(r => !r.vinculado || (confirmarPagamento && !r.pagamentoConfirmado));
                if (ok > 0) {
                    toast.success(confirmarPagamento
                        ? `${ok} vinculada(s) ao extrato; ${pagos} pagamento(s) confirmado(s).`
                        : `${ok} solicitação(ões) vinculada(s) ao extrato.`);
                }
                setFalhas(problemas.length > 0 ? problemas : null);
                setConfirmando(false);
                setComentario("");
                analisar();
                aoAlterar?.();
            })
            .catch(() => { /* mensagem do backend exibida pelo interceptor */ })
            .finally(() => setVinculando(false));
    }

    const valorSelecionado = selecionadas.reduce((s, x) => s + (Number(x.linha.valor) || 0), 0);

    return (
        <div className="flex flex-col gap-4">
            <section className="rounded-xl border border-border bg-card p-4">
                <p className="text-sm text-muted-foreground mb-3">
                    Envie o extrato do banco em Excel (A: data do pagamento, H: histórico, I: valor, J: tipo C/D,
                    K: detalhamento). O sistema procura, entre as solicitações aguardando pagamento, a que tem o
                    <strong> mesmo valor</strong> e confere o <strong>fornecedor</strong> (CNPJ ou nome, mesmo abreviado)
                    e a <strong>data</strong> (que pode variar em relação ao vencimento). Nada é gravado até você vincular.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                    <input ref={inputArquivo} type="file" accept=".xlsx,.xls" className="hidden"
                        onChange={e => { setArquivo(e.target.files?.[0] || null); setAnalise(null); }} />
                    <Button variant="outline" label="Escolher extrato (.xlsx)" iconLeft="fa fa-file-excel"
                        onClick={() => inputArquivo.current?.click()} />
                    <span className="text-sm text-muted-foreground break-all">{arquivo ? arquivo.name : "Nenhum arquivo selecionado"}</span>
                    <Button label="Analisar extrato" iconLeft="fa fa-magnifying-glass" loading={analisando}
                        disabled={!arquivo} onClick={() => analisar()} />
                </div>
            </section>

            {analise && (
                <>
                    <div className="flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-muted px-3 py-1">{analise.totalDebitos} débito(s) no extrato</span>
                        <span className="rounded-full bg-success/10 text-success px-3 py-1">{analise.sugeridas} com sugestão</span>
                        <span className="rounded-full bg-destructive/10 text-destructive px-3 py-1">{analise.semCorrespondencia} sem correspondência</span>
                        <span className="rounded-full bg-muted px-3 py-1">{analise.jaVinculadas} já vinculada(s)</span>
                        <span className="rounded-full bg-muted px-3 py-1">{analise.creditosIgnorados} crédito(s) ignorado(s)</span>
                        <span className="rounded-full bg-amber-100 text-amber-700 px-3 py-1">
                            {analise.pendentesSemExtrato.length} solicitação(ões) em pagamento sem débito no extrato
                        </span>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap gap-1.5">
                            {FILTROS.map(f => (
                                <button key={f.chave} type="button" onClick={() => setFiltro(f.chave)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium border ${filtro === f.chave
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "border-border hover:bg-muted"}`}>
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button variant="outline" label="Marcar só as confiáveis" onClick={marcarConfiaveis} />
                            <Button label={`Vincular selecionadas (${selecionadas.length})`} iconLeft="fa fa-link"
                                disabled={selecionadas.length === 0} onClick={pedirConfirmacao} />
                        </div>
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-border bg-card">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/60 text-left text-xs">
                                <tr>
                                    <th className="px-3 py-2 w-8"></th>
                                    <th className="px-3 py-2">Data</th>
                                    <th className="px-3 py-2">Extrato</th>
                                    <th className="px-3 py-2 text-right">Valor</th>
                                    <th className="px-3 py-2">Solicitação</th>
                                    <th className="px-3 py-2">Vencimento</th>
                                    <th className="px-3 py-2">Confiança</th>
                                </tr>
                            </thead>
                            <tbody>
                                {linhasVisiveis.map(l => {
                                    const escolha = escolhas[l.linha];
                                    const opcoes = opcoesDaLinha(l);
                                    const atual = opcoes.find(o => o.solicitacaoId === escolha?.solicitacaoId) || null;
                                    const duplicada = atual && escolha?.marcada && repetidas.has(atual.solicitacaoId);
                                    return (
                                        <tr key={`${l.linha}-${l.ocorrencia}`} className={`border-t border-border align-top ${duplicada ? "bg-destructive/5" : ""}`}>
                                            <td className="px-3 py-2">
                                                {l.situacao === "SUGERIDA" && (
                                                    <input type="checkbox" className="h-4 w-4 accent-primary"
                                                        checked={!!escolha?.marcada} disabled={!escolha?.solicitacaoId}
                                                        onChange={() => alternarMarca(l)} />
                                                )}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">{formatarData(l.dataPagamento)}</td>
                                            <td className="px-3 py-2 max-w-[360px]">
                                                <p className="font-medium">{l.historico || "-"}</p>
                                                <p className="text-xs text-muted-foreground break-words">{l.detalhamento || "-"}</p>
                                            </td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap">{formatarMoeda(l.valor)}</td>
                                            <td className="px-3 py-2 min-w-[260px]">
                                                {l.situacao === "SUGERIDA" && (
                                                    <>
                                                        <select className="w-full border border-border rounded-md px-2 py-1 text-xs bg-card"
                                                            value={escolha?.solicitacaoId ?? ""}
                                                            onChange={e => trocarSolicitacao(l, e.target.value)}>
                                                            {opcoes.map(o => (
                                                                <option key={o.solicitacaoId} value={o.solicitacaoId}>
                                                                    {o.numero} — {o.fornecedorNome || "sem fornecedor"}
                                                                </option>
                                                            ))}
                                                            <option value="">— não vincular —</option>
                                                        </select>
                                                        {atual && (
                                                            <p className="text-[11px] mt-1">
                                                                <LinkSolicitacao id={atual.solicitacaoId} numero={`Abrir ${atual.numero}`} />
                                                            </p>
                                                        )}
                                                        {duplicada && (
                                                            <p className="text-[11px] text-destructive mt-1">Escolhida para mais de uma linha.</p>
                                                        )}
                                                    </>
                                                )}
                                                {l.situacao === "JA_VINCULADA" && (
                                                    <span className="text-xs text-muted-foreground">
                                                        Já vinculada a{" "}
                                                        <LinkSolicitacao id={l.vinculadaA?.solicitacaoId} numero={l.vinculadaA?.numero} />
                                                    </span>
                                                )}
                                                {l.situacao === "SEM_CORRESPONDENCIA" && (
                                                    <span className="text-xs text-muted-foreground">{l.criterio}</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                {atual?.vencimento ? formatarData(atual.vencimento) : "-"}
                                            </td>
                                            <td className="px-3 py-2">
                                                {l.situacao === "SUGERIDA" && atual && (
                                                    <>
                                                        <Selo confianca={atual.confianca} ambigua={l.ambigua && atual.solicitacaoId === l.sugerida?.solicitacaoId} />
                                                        <p className="text-[11px] text-muted-foreground mt-1 max-w-[260px]">{atual.criterio}</p>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {linhasVisiveis.length === 0 && (
                                    <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Nenhuma linha neste filtro.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {analise.pendentesSemExtrato.length > 0 && (
                        <details className="rounded-xl border border-border bg-card p-3 text-sm">
                            <summary className="cursor-pointer font-medium">
                                Solicitações em pagamento sem débito correspondente no extrato ({analise.pendentesSemExtrato.length})
                            </summary>
                            <ul className="mt-2 divide-y divide-border">
                                {analise.pendentesSemExtrato.map(p => (
                                    <li key={p.solicitacaoId} className="py-1.5 flex flex-wrap justify-between gap-2">
                                        <span><LinkSolicitacao id={p.solicitacaoId} numero={p.numero} /> — {p.fornecedorNome || "sem fornecedor"}</span>
                                        <span className="text-muted-foreground">
                                            {formatarMoeda(p.valor)} · venc. {formatarData(p.vencimento)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </>
            )}

            <Dialog header="Vincular ao extrato" visible={confirmando} onHide={() => !vinculando && setConfirmando(false)}
                className="w-[95%] md:w-[520px]" blockScroll>
                <p className="text-sm mb-3">
                    Vincular <strong>{selecionadas.length}</strong> solicitação(ões) ao extrato, totalizando{" "}
                    <strong>{formatarMoeda(valorSelecionado)}</strong>.
                </p>
                <label className="flex items-start gap-2 text-sm mb-3">
                    <input type="checkbox" className="mt-1 accent-primary" checked={confirmarPagamento}
                        onChange={e => setConfirmarPagamento(e.target.checked)} />
                    <span>
                        Confirmar também o pagamento (conclui a etapa de Confirmação de Pagamento, sem pendência).
                        Sem esta opção, só o vínculo com o extrato é gravado.
                    </span>
                </label>
                {confirmarPagamento && (
                    <textarea rows={2} className="w-full border border-border rounded-md px-2 py-1.5 text-sm bg-card mb-3"
                        placeholder="Comentário (opcional) — padrão: Pagamento confirmado pelo extrato bancário."
                        value={comentario} onChange={e => setComentario(e.target.value)} />
                )}
                <div className="flex justify-end gap-2">
                    <Button variant="outline" label="Cancelar" disabled={vinculando} onClick={() => setConfirmando(false)} />
                    <Button label="Vincular" iconLeft="fa fa-link" loading={vinculando} onClick={vincular} />
                </div>
            </Dialog>

            <Dialog header="Pontos de atenção" visible={!!falhas} onHide={() => setFalhas(null)}
                className="w-[95%] md:w-[560px]" blockScroll>
                <ul className="divide-y divide-border text-sm">
                    {(falhas || []).map(f => (
                        <li key={f.solicitacaoId} className="py-2">
                            <LinkSolicitacao id={f.solicitacaoId} numero={f.numero} /> — {f.mensagem}
                        </li>
                    ))}
                </ul>
            </Dialog>
        </div>
    );
}
